import { Loader2, Users } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import type { AccountRole, ManagedUser, UserSummary, ConvertibleAccountType } from "./types";
import type { Trial } from "./trials-api";
import { UserTableRow } from "./UserTableRow";

type UserTableProps = {
  users: ManagedUser[];
  actionUserId: string | null;
  summary: UserSummary;
  isLoading?: boolean;
  hasSearch?: boolean;
  trials?: Map<string, Trial>;
  onEdit: (user: ManagedUser) => void;
  onResetPassword: (user: ManagedUser) => void;
  onRevokeSessions: (user: ManagedUser) => void;
  onAccountRoleChange: (userId: string, role: AccountRole) => void;
  onAccountTypeChange: (userId: string, type: ConvertibleAccountType) => void;
  onBanToggle: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
  onClearSearch?: () => void;
};

export const UserTable = ({
  users,
  actionUserId,
  isLoading,
  hasSearch,
  trials,
  onEdit,
  onResetPassword,
  onRevokeSessions,
  onAccountRoleChange,
  onAccountTypeChange,
  onBanToggle,
  onDelete,
  onClearSearch,
}: UserTableProps) => {
  return (
    <div className="relative min-h-0 flex-1 overflow-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="sticky top-0 z-10 border-b border-border/60 bg-card/95 backdrop-blur hover:bg-transparent">
            <TableHead className="sticky left-0 z-20 min-w-[200px] border-r border-border/60 bg-card/95 px-3 text-xs font-semibold uppercase tracking-[0.22em] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] backdrop-blur">
              User
            </TableHead>
            <TableHead className="w-32 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Username
            </TableHead>
            <TableHead className="w-24 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Roles
            </TableHead>
            <TableHead className="w-20 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Type
            </TableHead>
            <TableHead className="w-24 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Co-teachers
            </TableHead>
            <TableHead className="w-28 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Providers
            </TableHead>
            <TableHead className="w-20 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Status
            </TableHead>
            <TableHead className="w-28 px-3 text-xs font-semibold uppercase tracking-[0.22em]">
              Created
            </TableHead>
            <TableHead className="w-20 px-3 text-right text-xs font-semibold uppercase tracking-[0.22em]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 && isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <Skeleton className="size-8 rounded-full" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3.5 w-28" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-3"><Skeleton className="h-3.5 w-24" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-5 w-12 rounded-full" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-3.5 w-8" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
                <TableCell className="px-3"><Skeleton className="h-3.5 w-20" /></TableCell>
                <TableCell className="px-3 text-right"><Skeleton className="h-7 w-7 ml-auto" /></TableCell>
              </TableRow>
            ))
          ) : users.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell colSpan={9} className="h-40 text-center">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <div className="rounded-full bg-muted/40 p-3">
                    <Users className="h-10 w-10 opacity-60" />
                  </div>
                  <p>No users found</p>
                  {hasSearch && onClearSearch && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onClearSearch}
                    >
                      Clear search
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <UserTableRow
                key={user.id}
                user={user}
                isActionLoading={actionUserId === user.id}
                trial={trials?.get(user.id) ?? null}
                onEdit={onEdit}
                onResetPassword={onResetPassword}
                onRevokeSessions={onRevokeSessions}
                onAccountRoleChange={onAccountRoleChange}
                onAccountTypeChange={onAccountTypeChange}
                onBanToggle={onBanToggle}
                onDelete={onDelete}
              />
            ))
          )}
        </TableBody>
      </Table>
      {isLoading && (
        <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md border border-border/60 bg-background/90 px-3 py-2 text-sm">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Refreshing...</span>
        </div>
      )}
    </div>
  );
};
